import React from 'react';

/**
 * DiagnosisAutocomplete - read-only rendering of saved diagnosis selections.
 *
 * The field type is retired: it is stripped from every unsigned note's
 * effective schema (useNoteTemplate strips it; NoteDiagnosesSection owns dx
 * on encounter-less notes and Services & Diagnoses owns it on encounter
 * notes) and can't be added in the template editor (RETIRED_FIELD_TYPES).
 * This component exists only so signed notes still display the dx values
 * frozen into their structured_data. It makes no API calls and edits nothing.
 */
const DiagnosisAutocomplete = ({ field, value, error = null }) => {
  const diagnoses = Array.isArray(value)
    ? value
    : value && typeof value === 'object'
      ? [value]
      : [];

  return (
    <div className="diagnosis-autocomplete">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
      {diagnoses.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No diagnoses recorded.</p>
      ) : (
        <ul className="space-y-2">
          {diagnoses.map((diagnosis, index) => {
            const key = diagnosis.diagnosis_id || diagnosis.icd_code_id || `dx-${index}`;
            const code = diagnosis.icd_code || diagnosis.code;
            return (
              <li
                key={key}
                className="border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 bg-gray-50 dark:bg-gray-800"
              >
                <div className="flex items-center gap-2">
                  {code && (
                    <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {code}
                    </span>
                  )}
                  {diagnosis.is_primary && (
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
                      PRIMARY
                    </span>
                  )}
                  {diagnosis.status && diagnosis.status !== 'active' && (
                    <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded">
                      {diagnosis.status === 'rule_out'
                        ? 'Rule out'
                        : diagnosis.status.charAt(0).toUpperCase() + diagnosis.status.slice(1)}
                    </span>
                  )}
                </div>
                {diagnosis.description && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                    {diagnosis.description}
                  </p>
                )}
                {diagnosis.notes && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                    {diagnosis.notes}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DiagnosisAutocomplete;
